"use client";

import {
  Button,
  Input,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@pex/ui-web";
import { Info } from "lucide-react";
import { useRouter } from "next/navigation";
import type React from "react";
import { useState } from "react";
import { isValidEmail } from "@/lib/validation";

interface CheckInFormProps {
  groupId: string | null;
  preserveGroupParam?: boolean;
}

export function CheckInForm({
  groupId,
  preserveGroupParam = false,
}: CheckInFormProps) {
  const router = useRouter();
  const [email, setEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSubmit = async (opts?: { noEmail?: boolean }) => {
    if (!groupId) {
      return;
    }

    if (!(opts?.noEmail || isValidEmail(email))) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError(null);

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/api/check-in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId,
          email: opts?.noEmail ? undefined : email,
          preserveGroupParam,
        }),
        redirect: "follow",
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError((data as { error?: string }).error ?? "Something went wrong");
        setSubmitting(false);
        return;
      }

      const dest = new URL(response.url);
      if (dest.pathname === "/complete") {
        setIsExiting(true);
        setTimeout(() => router.push(response.url), 350);
      } else {
        // Keep submitting=true — component unmounts on navigation, no flash
        router.push(response.url);
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && email) {
      e.preventDefault();
      handleSubmit().catch(() => undefined);
    }
  };

  return (
    <>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit().catch(() => undefined);
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="email-address"
            >
              Email Address
            </label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Why we collect your email"
                    className="cursor-pointer text-muted-foreground hover:text-foreground"
                    type="button"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-56 text-center text-xs">
                  Capturing attendance helps our organisation secure funding.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Input
            className="h-12 text-base"
            disabled={submitting}
            id="email-address"
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="you@example.com"
            type="email"
            value={email}
          />
        </div>

        {emailError && <p className="text-destructive text-sm">{emailError}</p>}
        {error && <p className="text-destructive text-sm">{error}</p>}

        <Button
          className={`relative w-full overflow-hidden rounded-full ${submitting ? "disabled:opacity-100" : ""}`}
          disabled={submitting || !groupId || !email}
          size="lg"
          type="submit"
        >
          <span
            className={`flex items-center transition-all duration-200 ease-in-out ${
              submitting
                ? "-translate-y-6 opacity-0"
                : "translate-y-0 opacity-100"
            }`}
          >
            Check In →
          </span>
          <span
            className={`absolute inset-0 flex items-center justify-center gap-2 transition-all duration-200 ease-in-out ${
              submitting
                ? "translate-y-0 opacity-100"
                : "translate-y-6 opacity-0"
            }`}
          >
            <Spinner className="h-4 w-4" />
            Checking in
          </span>
        </Button>

        <Button
          className="text-muted-foreground"
          disabled={submitting || !groupId}
          onClick={() => handleSubmit({ noEmail: true }).catch(() => undefined)}
          type="button"
          variant="link"
        >
          I don&apos;t have an email address
        </Button>
      </form>
      {isExiting && (
        <div
          className="pointer-events-none fixed inset-0 z-50"
          style={{
            background:
              "linear-gradient(180deg, var(--brand-yellow-lightest) 0%, var(--background) 60%)",
            animation: "fade-in 0.35s ease-out both",
          }}
        />
      )}
    </>
  );
}
